import { BaseValidation } from './base-validation';
import { CodeValueMessageArg } from '../../../resources/code-value-message-arg';
import { MessageResourceManager } from '../../../resources/message-resource-manager';
import { BigNumber, CompareModes, OpenDirections, TimeSpan } from '@nts/std/types';
import { ValidationArguments } from 'class-validator';
import { BaseValidator } from '../commons/base-validator';

export class CompareValidator<TValue = Date | TimeSpan | BigNumber | number> {

    private readonly compareMode: CompareModes;
    private readonly openDirection: OpenDirections;


    private currentPropertyDisplayName: string;
    private comparePropertyDisplayName: string;

    errorMessage: string;
    messageCode: string;

    constructor(
        compareMode: CompareModes,
        openDirection: OpenDirections,
        currentPropertyDisplayName: string,
        comparePropertyDisplayName: string,
    ) {
        this.compareMode = compareMode;
        this.openDirection = openDirection;
        this.currentPropertyDisplayName = currentPropertyDisplayName;
        this.comparePropertyDisplayName = comparePropertyDisplayName;
    }

    validate(valueSource: TValue, valueToCompare: TValue): boolean {
        let isValid = true;

        if (valueSource == null && valueToCompare == null) {
            switch (this.openDirection) {
                case OpenDirections.Both:
                    return true;
                default:
                    this.errorMessage = this.getValidationError();
                    return false;
            }
        }

        if (valueSource == null) {
            switch (this.openDirection) {
                case OpenDirections.Self:
                case OpenDirections.Both:
                    return true;
                default:
                    this.errorMessage = this.getValidationError();
                    return false;
            }
        }

        if (valueToCompare == null) {
            switch (this.openDirection) {
                case OpenDirections.Other:
                case OpenDirections.Both:
                    return true;
                default:
                    this.errorMessage = this.getValidationError();
                    return false;
            }
        }

        return this.compareValue(valueSource, valueToCompare);

    }

    private compareValue(value: TValue, valueToCompare: TValue) {

        switch (this.compareMode) {
            case CompareModes.GreaterThan:
                if (this.compareTo(value, valueToCompare) <= 0) {
                    this.errorMessage = this.getValidationError();
                    return false;
                }

                break;
            case CompareModes.GreaterThanOrEqualTo:
                if (this.compareTo(value, valueToCompare) < 0) {
                    this.errorMessage = this.getValidationError();
                    return false;
                }

                break;
            case CompareModes.EqualTo:
                if (this.compareTo(value, valueToCompare) != 0) {
                    this.errorMessage = this.getValidationError();
                    return false;
                }

                break;
            case CompareModes.LessThanOrEqualTo:
                if (this.compareTo(value, valueToCompare) > 0) {
                    this.errorMessage = this.getValidationError();
                    return false;
                }
                break;
            case CompareModes.LessThan:
                if (this.compareTo(value, valueToCompare) >= 0) {
                    this.errorMessage = this.getValidationError();
                    return false;
                }

                break;
        }

        return true;
    }

    /**
     * Compares two values.
     * @param value The value to compare.
     * @param valueToCompare The value to compare against.
     */
    private compareTo(value: TValue, valueToCompare: TValue): number {

        if (value instanceof BigNumber && valueToCompare instanceof BigNumber) {
            return value.comparedTo(valueToCompare);
        }

        if (value instanceof TimeSpan && valueToCompare instanceof TimeSpan) {
            return value.compareTo(valueToCompare);
        }

        if (value instanceof Date && valueToCompare instanceof Date) {
            return value.getTime() - valueToCompare.getTime();
        }

        if (value === valueToCompare) {
            return 0;
        }
        if (value == null) {
            return -1;
        }
        if (valueToCompare == null) {
            return 1;
        }
        return value < valueToCompare ? -1 : 1;
    }


    private getValidationError(): string {
        const currentPropertyDisplayName: string = this.currentPropertyDisplayName;
        const propertyToCompareDisplayName: string = this.comparePropertyDisplayName;
        return MessageResourceManager.Current.getMessageWithArgs(
            "std_CompareValidationAttributeError",
            [
                {
                    code: "sourceProperty",
                    value: currentPropertyDisplayName,
                },
                {
                    code: "compareOperator",
                    value: MessageResourceManager.Current.getMessage(`CompareModes_${CompareModes[this.compareMode]}`),
                },
                {
                    code: "compareProperty",
                    value: propertyToCompareDisplayName,
                },
            ]
        );
    }

    getMessageWithTag(value: string, tag: string, messageCode: string): string {
        return this.getMessageWithCodeValues(messageCode, [{ code: tag, value }]);
    }

    getMessageWithCodeValues(baseMessage: string, tags: { code: string, value: string; }[]): string {
        const arr = new Array<CodeValueMessageArg>();
        for (const tag of tags) {
            const arg = new CodeValueMessageArg();
            arg.code = tag.code;
            arg.value = tag.value;
            arr.push(arg);
        }
        return MessageResourceManager.Current.getMessageWithArgs(baseMessage, arr);
    }
}

export class CompareValidation<TValue = Date | number | TimeSpan | BigNumber> extends BaseValidation<TValue> {

    private readonly compareMode: CompareModes;
    private readonly comparePropertyName: string;
    private readonly openDirection: OpenDirections;

    constructor(
        compareMode: CompareModes,
        comparePropertyName: string,
        openDirection: OpenDirections
    ) {
        super();
        this.compareMode = compareMode;
        this.openDirection = openDirection;
        this.comparePropertyName = comparePropertyName;
    }

    validateImplementation(value: TValue, args: ValidationArguments): boolean {
        const valueToCompare = args?.object[this.comparePropertyName];

        const valueToCompareDecoratorData = BaseValidator.getPropertyMetaData(args?.object, this.comparePropertyName);
        const valueToCompareDisplayName = MessageResourceManager.Current.getMessageIfExists(valueToCompareDecoratorData.descriptions.displayNameKey);
        const validator = new CompareValidator<TValue>(
            this.compareMode,
            this.openDirection,
            this.getDisplayName(args),
            valueToCompareDisplayName
        );
        const success = validator.validate(value, valueToCompare);
        if (!success) {
            this.messageCode = validator.messageCode;
            this.errorMessage = validator.errorMessage;
        }
        return success;
    }
}
